import { ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';

import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import ChipTsoPq from '@components/Chips/ChipsTsoPq';
import LoadingSpinnerTsoPq from '@components/LoadingSpinners/LoadingSpinnerTsoPq';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { defaultTsos } from '@pages/MeasurementVisualization/Section/MeasurementVisualizationParameters';
import { ApiCommonExternalDtoMeasurementLogExternalDto } from '@redux/apiSlices/measurementLogApi';
import { useTranslation } from 'react-i18next';
import {
  StyledChartContainer,
  StyledChartLabel,
  StyledChartLabelChip,
  StyledChartLabelPointAndSubstation,
  StyledChartLabelTypograpgies,
} from './importedMeasurementsOverviewStyles';

interface ImportedMeasurementsOverviewChartProps {
  deviceId: number;
  tsoId: number;
  measurementLogs: ApiCommonExternalDtoMeasurementLogExternalDto[];
  deviceLabel: string;
  measurementPoint: string;
  substationLabel: string;
  start: string;
  end: string;
}
export const getTsoMeta = (tsoId: number) => {
  const tso = defaultTsos.find((tso) => tso.id === tsoId);

  const color = (() => {
    switch (tsoId) {
      case 1:
        return tsoPqTheme.palette.background.tso.active.color_tso_background_50hertz;
      case 2:
        return tsoPqTheme.palette.background.tso.active.color_tso_background_amprion;
      case 3:
        return tsoPqTheme.palette.background.tso.active.color_tso_background_tennet;
      case 4:
        return tsoPqTheme.palette.background.tso.active.color_tso_background_transnet;
      default:
        return tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_accent;
    }
  })();

  return {
    name: tso?.name ?? 'Unknown',
    color,
  };
};

export const ImportedMeasurementsOverviewChart = ({
  deviceId,
  measurementLogs,
  tsoId,
  deviceLabel,
  measurementPoint,
  substationLabel,
  start,
  end,
}: ImportedMeasurementsOverviewChartProps) => {
  const { t } = useTranslation();

  const isLoading = useMemo(() => start === null || end === null, [start, end]);
  if (isLoading) {
    return <LoadingSpinnerTsoPq />;
  }

  const series = [
    {
      name: t('importedMeasurementOverview.recordedData'),
      data:
        measurementLogs.length > 0
          ? measurementLogs.map((log) => ({
              x: `${measurementPoint} (${deviceLabel})`,
              y: [dayjs.utc(log.start).local().valueOf(), dayjs.utc(log.end).local().valueOf()],
            }))
          : [
              {
                x: 'No data available',
                y: [],
              },
            ],
    },
  ];

  const options: ApexOptions = {
    chart: {
      type: 'rangeBar',
      animations: { enabled: false },
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    xaxis: {
      type: 'datetime',
      min: dayjs(start, 'DD.MM.YYYY HH:mm').add(-1, 'day').valueOf(),
      max: dayjs(end, 'DD.MM.YYYY HH:mm').add(1, 'day').valueOf(),
      labels: {
        style: { fontSize: '0.75rem' },
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '65%',
        rangeBarGroupRows: true,
      },
    },
    yaxis: {
      labels: {
        show: false,
      },
    },
    tooltip: {
      custom: ({ seriesIndex, dataPointIndex, w }) => {
        const dataPoint = w.config.series[seriesIndex].data[dataPointIndex];
        const startTime = dayjs(dataPoint.y[0]).format('DD.MM.YYYY HH:mm');
        const endTime = dayjs(dataPoint.y[1]).format('DD.MM.YYYY HH:mm');
        return `<strong>${dataPoint.x}: </strong> ${startTime} - ${endTime}`;
      },
    },
    states: {
      hover: {
        filter: { type: 'darken' },
      },
      active: {
        filter: { type: 'none' },
      },
    },
    fill: {
      type: 'solid',
      opacity: 1,
    },
    stroke: {
      width: 1,
    },
    colors: [tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_accent],
  };

  return (
    <StyledChartContainer>
      <StyledChartLabel>
        <StyledChartLabelChip>
          <ChipTsoPq textContent={getTsoMeta(tsoId).name} sx={{ backgroundColor: getTsoMeta(tsoId).color }} />
        </StyledChartLabelChip>
        <StyledChartLabelTypograpgies>
          <TypographyTsoPq typographyType="labelLGBold">{deviceLabel}</TypographyTsoPq>
          <StyledChartLabelPointAndSubstation>
            <TypographyTsoPq typographyType="labelMD">{measurementPoint}</TypographyTsoPq>
            <TypographyTsoPq typographyType="labelMD">{substationLabel}</TypographyTsoPq>
          </StyledChartLabelPointAndSubstation>
        </StyledChartLabelTypograpgies>
      </StyledChartLabel>
      <ReactApexChart
        options={options}
        series={series}
        type="rangeBar"
        height={120}
        width={1036}
        style={{ display: 'flex', alignItems: 'center' }}
      />
    </StyledChartContainer>
  );
};
